import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { label } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Zap, Trash2, Play, History, ArrowRight } from "lucide-react";

const scopeOf = (meta, event) => meta?.events?.find((e) => e.value === event)?.scope || "schedule";

export default function Automations() {
  const { user } = useAuth();
  const canManage = can(user, "automation:manage");
  const [meta, setMeta] = useState(null);
  const [rules, setRules] = useState([]);
  const [runs, setRuns] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const [form, setForm] = useState({
    name: "",
    trigger: "workorder.completed",
    condField: "",
    condOp: "eq",
    condValue: "",
    actionType: "create_work_order",
    params: {},
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    api.get("/automations").then(({ data }) => setRules(data));
    api.get("/automations/runs").then(({ data }) => setRuns(data));
  }, []);

  useEffect(() => {
    api.get("/automations/meta").then(({ data }) => setMeta(data));
    api.get("/properties").then(({ data }) => setProperties(data));
    load();
  }, [load]);

  const scope = scopeOf(meta, form.trigger);
  const fields = meta?.condition_fields?.[scope] || [];
  const actionDef = meta?.actions?.find((a) => a.value === form.actionType);

  const toggle = async (rule, enabled) => {
    setRules((list) => list.map((r) => (r._id === rule._id ? { ...r, enabled } : r)));
    try {
      await api.patch(`/automations/${rule._id}`, { enabled });
      toast.success(`${rule.name} ${enabled ? "enabled" : "paused"}`);
    } catch (err) {
      load();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update automation");
    }
  };

  const runNow = async (rule) => {
    try {
      const { data } = await api.post(`/automations/${rule._id}/run`);
      toast.success(data.outcomes?.join(" · ") || "Automation ran");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not run automation");
    }
  };

  const remove = async (rule) => {
    try {
      await api.delete(`/automations/${rule._id}`);
      toast.success("Automation deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete automation");
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/automations", {
        name: form.name,
        trigger: { event: form.trigger },
        conditions: form.condField ? [{ field: form.condField, op: form.condOp, value: form.condValue }] : [],
        actions: [{ type: form.actionType, params: form.params }],
      });
      toast.success("Automation created");
      setOpen(false);
      setForm({ name: "", trigger: "workorder.completed", condField: "", condOp: "eq", condValue: "",
        actionType: "create_work_order", params: {} });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create automation");
    }
    setBusy(false);
  };

  const describe = (rule) => {
    const event = meta?.events?.find((e) => e.value === rule.trigger?.event)?.label || rule.trigger?.event;
    const conds = (rule.conditions || []).map(
      (c) => `${label(c.field)} ${meta?.operators?.find((o) => o.value === c.op)?.label || c.op} ${
        Array.isArray(c.value) ? c.value.join(" / ") : c.value}`
    );
    const acts = (rule.actions || []).map(
      (a) => meta?.actions?.find((x) => x.value === a.type)?.label || a.type
    );
    return { event, conds, acts };
  };

  if (!meta) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  return (
    <div className="space-y-6" data-testid="automations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Runs itself</p>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-royal-deep mt-2">Automations</h1>
          <p className="text-muted-foreground mt-3 text-sm md:text-base">
            When something happens, if it matches your conditions, Cornerstone Ops does the work for you.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="toggle-runs-btn"
            variant="outline"
            onClick={() => setShowRuns((v) => !v)}
            className="h-11 gap-2 border-royal/30 text-royal hover:bg-royal/5"
          >
            <History className="h-4 w-4" /> {showRuns ? "Hide" : "Run"} history
          </Button>
          {canManage && (
            <Button
              data-testid="new-automation-btn"
              onClick={() => setOpen(true)}
              className="bg-royal hover:bg-royal-deep text-white gap-2 h-11"
            >
              <Plus className="h-4 w-4" /> New automation
            </Button>
          )}
        </div>
      </div>

      {showRuns && (
        <div className="rounded-lg border border-border bg-white divide-y divide-border" data-testid="automation-runs">
          {runs.length === 0 && <p className="p-5 text-sm text-muted-foreground">No automations have run yet.</p>}
          {runs.map((r) => (
            <div key={r._id} className="px-5 py-3.5" data-testid={`run-${r._id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Zap className="h-3.5 w-3.5 text-gold" />
                <p className="text-sm font-semibold text-royal-deep">{r.rule_name}</p>
                <span className="rounded-full border border-royal/15 bg-royal/5 px-2 py-0.5 text-[11px] text-royal">
                  {r.event}
                </span>
                <p className="ml-auto text-[11px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </p>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{(r.outcomes || []).join(" · ")}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2" data-testid="automations-list">
        {rules.map((rule) => {
          const d = describe(rule);
          return (
            <div
              key={rule._id}
              data-testid={`automation-card-${rule._id}`}
              className={`rounded-lg border bg-white p-5 transition-shadow duration-200 hover:shadow-md ${
                rule.enabled ? "border-gold/50" : "border-border opacity-80"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-display font-semibold text-royal-deep">{rule.name}</p>
                  {rule.description && (
                    <p className="text-xs text-muted-foreground mt-1">{rule.description}</p>
                  )}
                </div>
                {canManage && (
                  <Switch
                    data-testid={`toggle-automation-${rule._id}`}
                    checked={!!rule.enabled}
                    onCheckedChange={(v) => toggle(rule, v)}
                  />
                )}
              </div>

              <div className="mt-4 space-y-2 text-xs">
                <p className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-royal text-white px-1.5 py-0.5 font-semibold tracking-wide">WHEN</span>
                  <span className="text-royal-deep">{d.event}</span>
                </p>
                {d.conds.length > 0 && (
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gold text-royal-deep px-1.5 py-0.5 font-semibold tracking-wide">IF</span>
                    <span className="text-royal-deep">{d.conds.join(" and ")}</span>
                  </p>
                )}
                <p className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-royal-deep text-white px-1.5 py-0.5 font-semibold tracking-wide">THEN</span>
                  <span className="text-royal-deep flex items-center gap-1.5">
                    {d.acts.map((a, idx) => (
                      <span key={idx} className="flex items-center gap-1.5">
                        {idx > 0 && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
                        {a}
                      </span>
                    ))}
                  </span>
                </p>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <p className="text-[11px] text-muted-foreground">
                  Ran {rule.run_count || 0} time{rule.run_count === 1 ? "" : "s"}
                  {rule.last_run_at ? ` · last ${new Date(rule.last_run_at).toLocaleDateString()}` : ""}
                </p>
                {canManage && (
                  <div className="ml-auto flex gap-2">
                    <Button
                      data-testid={`run-automation-${rule._id}`}
                      variant="outline"
                      onClick={() => runNow(rule)}
                      className="h-9 text-xs gap-1.5 border-royal/30 text-royal hover:bg-royal/5"
                    >
                      <Play className="h-3 w-3" /> Run now
                    </Button>
                    {!rule.system && (
                      <Button
                        data-testid={`delete-automation-${rule._id}`}
                        variant="outline"
                        onClick={() => remove(rule)}
                        className="h-9 text-xs gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/5"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="automation-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Build an automation</DialogTitle>
            <DialogDescription>Choose a trigger, narrow it with a condition, then pick what happens.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="automation-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
              <Input data-testid="automation-name-input" required value={form.name}
                onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11"
                placeholder="e.g. Restock when supplies run low" />
            </div>

            <div className="rounded-md border border-royal/20 bg-royal/5 p-3">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-royal">When</Label>
              <Select value={form.trigger} onValueChange={(v) => setForm((f) => ({ ...f, trigger: v, condField: "" }))}>
                <SelectTrigger data-testid="automation-trigger-select" className="mt-1.5 h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meta.events.map((e) => (
                    <SelectItem key={e.value} value={e.value} data-testid={`trigger-option-${e.value}`}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {fields.length > 0 && (
              <div className="rounded-md border border-gold/40 bg-gold/5 p-3">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-royal">If (optional)</Label>
                <div className="mt-1.5 grid gap-2 sm:grid-cols-3">
                  <Select value={form.condField || "none"}
                    onValueChange={(v) => set("condField", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="automation-cond-field" className="h-11 bg-white">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Always run</SelectItem>
                      {fields.map((f) => (
                        <SelectItem key={f.value} value={f.value} data-testid={`cond-field-${f.value}`}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={form.condOp} onValueChange={(v) => set("condOp", v)}>
                    <SelectTrigger data-testid="automation-cond-op" className="h-11 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meta.operators.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const def = fields.find((f) => f.value === form.condField);
                    if (def?.value === "property_id") {
                      return (
                        <Select value={form.condValue} onValueChange={(v) => set("condValue", v)}>
                          <SelectTrigger data-testid="automation-cond-value" className="h-11 bg-white">
                            <SelectValue placeholder="Property" />
                          </SelectTrigger>
                          <SelectContent>
                            {properties.map((p) => (
                              <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                    if (def?.options) {
                      return (
                        <Select value={form.condValue} onValueChange={(v) => set("condValue", v)}>
                          <SelectTrigger data-testid="automation-cond-value" className="h-11 bg-white">
                            <SelectValue placeholder="Value" />
                          </SelectTrigger>
                          <SelectContent>
                            {def.options.map((o) => (
                              <SelectItem key={o} value={o}>{label(o)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      );
                    }
                    return (
                      <Input data-testid="automation-cond-value" value={form.condValue}
                        onChange={(e) => set("condValue", e.target.value)} placeholder="Value"
                        className="h-11 bg-white" />
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="rounded-md border border-royal-deep/20 bg-secondary/60 p-3">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-royal">Then</Label>
              <Select value={form.actionType}
                onValueChange={(v) => setForm((f) => ({ ...f, actionType: v, params: {} }))}>
                <SelectTrigger data-testid="automation-action-select" className="mt-1.5 h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meta.actions.map((a) => (
                    <SelectItem key={a.value} value={a.value} data-testid={`action-option-${a.value}`}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {(actionDef?.params || []).map((p) =>
                  p.options ? (
                    <Select
                      key={p.key}
                      value={form.params[p.key] || ""}
                      onValueChange={(v) => set("params", { ...form.params, [p.key]: v })}
                    >
                      <SelectTrigger data-testid={`param-${p.key}`} className="h-11 bg-white">
                        <SelectValue placeholder={p.label} />
                      </SelectTrigger>
                      <SelectContent>
                        {p.options.map((o) => (
                          <SelectItem key={o} value={o}>{label(o)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      key={p.key}
                      data-testid={`param-${p.key}`}
                      value={form.params[p.key] || ""}
                      onChange={(e) => set("params", { ...form.params, [p.key]: e.target.value })}
                      placeholder={p.label}
                      className="h-11 bg-white"
                    />
                  )
                )}
              </div>
            </div>

            <Button data-testid="save-automation-btn" type="submit" disabled={busy || !form.name}
              className="w-full h-12 bg-royal hover:bg-royal-deep text-white font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create automation"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
